import { useState } from 'react';
import {
  Tab as MaterialTab,
  Tabs as MaterialTabs,
  Tooltip,
} from '@material-ui/core';
import makeStyles from '@material-ui/styles/makeStyles';
import PropTypes from 'prop-types';
import {
  darkNavyColor,
  grayColor,
  navyColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';

import ConditionalWrapper from '../ConditionalWrapper/ConditionalWrapper';

const useStyles = makeStyles({
  tabsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '23px',
  },
  tabsRoot: {
    minHeight: '0',
    backgroundColor: 'rgb(252, 251, 252)',
    borderRadius: '6px',
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.14)',
    padding: '2px',
    marginRight: '10px',
  },
  scrollable: {
    overflowX: 'auto',
  },
  tabRoot: {
    textTransform: 'inherit',
    fontSize: '12px',
    fontWeight: '400',
    color: grayColor[0],
    minWidth: '0',
    minHeight: '0',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: grayColor[8],
      color: navyColor,
    },
  },
  labelContainer: {
    padding: '0',
  },
  labelContent: {
    display: 'inline-block',
    padding: '9px 16px',
  },
  selected: {
    backgroundColor: navyColor,
    color: whiteColor,
    '&:hover': {
      backgroundColor: darkNavyColor,
      color: whiteColor,
    },
  },
  tooltip: {
    fontSize: '14px',
    margin: '0',
  },
});

const Tabs = ({
  tabs,
  contentAfterTabButtons,
  activeIndex,
  setActiveIndex,
}) => {
  const classes = useStyles();

  const [reloadKey, setReloadKey] = useState(0);

  const handleChange = (_, index) => {
    setReloadKey(prevKey => prevKey + 1);
    setActiveIndex(index);
  };

  return (
    <>
      <div className={classes.tabsWrapper}>
        <MaterialTabs
          classes={{
            root: classes.tabsRoot,
            flexContainer: classes.scrollable,
          }}
          value={activeIndex}
          onChange={handleChange}
          TabIndicatorProps={{
            style: {
              display: 'none',
            },
          }}
        >
          {tabs.map(({ tooltip, label, isDisabled = false }) => (
            <MaterialTab
              disabled={isDisabled}
              label={
                <ConditionalWrapper
                  useWrapper={!!tooltip}
                  renderWrapper={children => (
                    <Tooltip
                      title={tooltip}
                      placement="top"
                      classes={{
                        tooltip: classes.tooltip,
                      }}
                      PopperProps={{
                        modifiers: {
                          offset: {
                            enabled: true,
                            offset: '0 4px',
                          },
                        },
                      }}
                    >
                      {children}
                    </Tooltip>
                  )}
                >
                  <span className={classes.labelContent}>{label}</span>
                </ConditionalWrapper>
              }
              key={label}
              classes={{
                root: classes.tabRoot,
                labelContainer: classes.labelContainer,
                selected: classes.selected,
              }}
            />
          ))}
        </MaterialTabs>
        {contentAfterTabButtons}
      </div>
      <div key={reloadKey}>{tabs[activeIndex].content}</div>
    </>
  );
};

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
      isDisabled: PropTypes.bool,
      tooltip: PropTypes.string,
    })
  ).isRequired,
  activeIndex: PropTypes.number.isRequired,
  setActiveIndex: PropTypes.func.isRequired,
  contentAfterTabButtons: PropTypes.node,
};

export default Tabs;
